import { TagRow } from '@/components/builder/TagRow'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export type UtmKey =
  | 'source'
  | 'medium'
  | 'campaign'
  | 'content'
  | 'term'
  | 'id'
  | 'source_platform'
  | 'creative_format'
  | 'marketing_tactic'

export type UtmValues = Partial<Record<UtmKey, string>>

interface UtmSectionProps {
  values: UtmValues
  onAdd: (key: UtmKey) => void
  onChange: (key: UtmKey, value: string) => void
  onRemove: (key: UtmKey) => void
}

interface UtmFieldDef {
  key: UtmKey
  label: string
  description: string
}

// Order is intentional: classic five first (most common), GA4 additions after.
const FIELDS: ReadonlyArray<UtmFieldDef> = [
  {
    key: 'source',
    label: 'Source',
    description: 'Identifies the referrer, e.g. google, newsletter, facebook.',
  },
  {
    key: 'medium',
    label: 'Medium',
    description: 'The marketing medium, e.g. cpc, email, social.',
  },
  {
    key: 'campaign',
    label: 'Campaign',
    description: 'Product, promo code or strategic campaign name.',
  },
  {
    key: 'content',
    label: 'Content',
    description: 'Differentiates ads or links pointing to the same URL.',
  },
  {
    key: 'term',
    label: 'Term',
    description: 'Identifies paid search keywords.',
  },
  {
    key: 'id',
    label: 'Campaign ID',
    description: 'The unique campaign identifier (utm_id in GA4).',
  },
  {
    key: 'source_platform',
    label: 'Source platform',
    description: 'Where the visit originated, e.g. the ad platform name.',
  },
  {
    key: 'creative_format',
    label: 'Creative format',
    description: 'The format of the ad creative, e.g. display, native, video.',
  },
  {
    key: 'marketing_tactic',
    label: 'Marketing tactic',
    description: 'Targeting criteria, e.g. remarketing, prospecting.',
  },
]

export function UtmSection({ values, onAdd, onChange, onRemove }: UtmSectionProps) {
  const added = FIELDS.filter((f) => f.key in values)
  const available = FIELDS.filter((f) => !(f.key in values))

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium">Standard UTM Parameters</h2>

      {added.length > 0 && (
        <div className="space-y-2.5">
          {added.map((field) => (
            <TagRow
              key={field.key}
              label={field.label}
              queryKey={`utm_${field.key}`}
              description={field.description}
              value={values[field.key] ?? ''}
              onChange={(value) => onChange(field.key, value)}
              onRemove={() => onRemove(field.key)}
            />
          ))}
        </div>
      )}

      {available.length > 0 ? (
        <Select value="" onValueChange={(value) => onAdd(value as UtmKey)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Add a standard UTM parameter…" />
          </SelectTrigger>
          <SelectContent>
            {available.map((field) => (
              <SelectItem key={field.key} value={field.key}>
                {field.label}{' '}
                <span className="text-muted-foreground">— utm_{field.key}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-xs text-muted-foreground">All standard UTM parameters added.</p>
      )}
    </section>
  )
}
